import { isNull } from "lodash";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useAlert } from "react-alert";
import { FormattedMessage, useIntl } from "react-intl";
import { useLazyQuery } from "@apollo/client";
import { CircularProgress, Dialog, Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  CREATE_ACCOUNT_SETTINGS,
  EDIT_ACCOUNT_SETTINGS,
} from "../../../../graphql/queries/CompanyAdminContext";
import DraggableModalBody from "../../../common/DraggableModal/DraggableModalBody";
import DraggableModalHead from "../../../common/DraggableModal/DraggableModalHead";
import AccountSettings from "./AccountSettings";

const useStyles = makeStyles(theme => ({
  modal: {
    width: "1200px",
    [theme.breakpoints.down("md")]: {
      width: "90%",
    },
    "& .MuiCircularProgress-root": {
      margin: "auto",
      position: "absolute",
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
    },
  },
  body: {
    padding: 0,
    minHeight: "75vh",
  },
}));

const AccountSettingsModal = ({ companyId, handleClose, open, selectedUserId }) => {
  const classes = useStyles();
  const intl = useIntl();
  const alert = useAlert();

  const [accountSettings, setAccountSettings] = useState(null);

  const [
    getDefaultAccountSettings,
    { data: defaultAccountSettings, error: errorDefaultAccountSettings },
  ] = useLazyQuery(CREATE_ACCOUNT_SETTINGS);

  const [
    getUserAccountSettings,
    { data: userAccountSettings, error: errorUserAccountSettings },
  ] = useLazyQuery(EDIT_ACCOUNT_SETTINGS);

  const { firstname, lastname } = (accountSettings || {});

  const contactInfoText = intl.formatMessage({
    id: "account.contactInformation",
  });

  const headerText = () => {
    let title = "";

    if (firstname) {
      title = `${firstname}`;
      if (lastname) {
        title = `${firstname} ${lastname}`;
      }
      title = `${title} - ${contactInfoText}`;
    } else if (lastname) {
      title = `${lastname} - ${contactInfoText}`;
    } else {
      title = `${contactInfoText}`;
    }
    return title;
  };

  useEffect(() => {
    if (open) {
      if (isNull(selectedUserId)) {
        getDefaultAccountSettings({ variables: { companyId } });
      } else {
        
        getUserAccountSettings({ variables: { userId: selectedUserId } });
      }
    }
  }, [open, companyId, selectedUserId]);

  useEffect(() => {
    if (
      defaultAccountSettings &&
      defaultAccountSettings.companyAdminContext &&
      defaultAccountSettings.companyAdminContext.users &&
      defaultAccountSettings.companyAdminContext.users.createAccountSettings
    ) {
      setAccountSettings(
        defaultAccountSettings.companyAdminContext.users.createAccountSettings
      );
    }
  }, [defaultAccountSettings]);

  useEffect(() => {
    if (
      userAccountSettings &&
      userAccountSettings.companyAdminContext &&
      userAccountSettings.companyAdminContext.users &&
      userAccountSettings.companyAdminContext.users.editAccountSettings
    ) {
      setAccountSettings(
        userAccountSettings.companyAdminContext.users.editAccountSettings
      );
    }
  }, [userAccountSettings]);

  useEffect(() => {
    if (errorDefaultAccountSettings || errorUserAccountSettings) {
      console.info("** failed to get account settings");
      alert.error(
        intl.formatMessage({ id: "userAccount.errors.genericError" })
      );
      handleClose?.();
    }
  }, [errorDefaultAccountSettings, errorUserAccountSettings]);

  const handleDirectMessageRequest = () => {
    console.log("** [AccountSettingsModal] handleDirectMessageRequest - TODO");
  };

  const haveError = errorDefaultAccountSettings || errorUserAccountSettings;

  return (
    <Dialog
      open={open}
      handleClose={handleClose}
      customClass={classes.modal}
      fullScreen
    >
      <DraggableModalHead handleClose={handleClose} title={headerText()} />
      <DraggableModalBody customClass={classes.body}>
        {haveError && (
          <Grid container justifyContent="center" alignItems="center">
            <FormattedMessage id="userAccount.errors.genericError" />
          </Grid>
        )}
        {!accountSettings && !haveError && (
          <Grid container justifyContent="center" alignItems="center">
            <CircularProgress size={50} />
          </Grid>
        )}
        {accountSettings && !haveError && (
          <AccountSettings
            handleClose={handleClose}
            onDirectMessageRequest={handleDirectMessageRequest}
            selectedUserId={selectedUserId}
            settings={accountSettings}
          />
        )}
      </DraggableModalBody>
    </Dialog>
  );
};

AccountSettingsModal.defaultProps = {
  selectedUser: null,
};

AccountSettingsModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  selectedUser: PropTypes.number,
};

export default AccountSettingsModal;
